import { html, LitElement } from 'lit'
import { customElement } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ref } from 'lit/directives/ref.js'

import { NotificationMixin } from '../common/mixins/NotificationMixin.js'
import componentStyle from '../common/styles/Component.css'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Notification.css'
import '../stack/Stack.js'
import '../icon/Icon.js'

/**
 * Notifications provide important information that requires action or acknowledgement.
 * A notification is displayed until the user dismisses it.
 *
 * @status ready
 * @category feedback
 * @slot - Default slot used for the notification text/message.
 * @slot icon - Slot used for the notification icon.
 * @fires dismiss - Fired when the notification is dismissed, and its exit animation has completed. This event should be used to remove the dismissed notification from the DOM.
 * @localization dismissLabel - Accessible label for the dismiss button.
 */
@customElement('nord-notification')
export default class Notification extends NotificationMixin(LitElement) {
  static styles = [componentStyle, style]

  private localize = new LocalizeController<'nord-notification'>(this)

  render() {
    return html`
      <div ${ref(this.notificationRef)} class=${classMap({ 'n-notification': true, 'n-dismissed': this.dismissed })}>
        <button class="n-dismiss" @click=${this.dismiss}>
          <nord-icon name="interface-close-small" size="xs" label=${this.localize.term('dismissLabel')}></nord-icon>
        </button>

        <nord-stack align-items="stretch" direction="horizontal">
          <slot name="icon">
            <nord-icon name="navigation-notifications"></nord-icon>
          </slot>
          <div class="n-notification-body">
            <slot></slot>
          </div>
        </nord-stack>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-notification': Notification
  }
}
